import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Package, ArrowLeft, IndianRupee,
  Activity, Plus,
} from "lucide-react";
import { toast } from "sonner";

import { v2Api } from "@/lib/v2Api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const fmtINR = (n) =>
  n == null ? "—" : new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n);

const todayISO = () => new Date().toISOString().slice(0, 10);

// --------------------------------------------------------------------------
// Rates tab
// --------------------------------------------------------------------------

function SkuRatesPanel({ sku }) {
  const [rates, setRates] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    valid_from: todayISO(),
    valid_to: "",
    list_price_inr: "",
    base_price_inr: "",
    channel: "default",
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try { setRates(await v2Api.catalog.listSkuRates(sku.id) || []); }
    catch { setRates([]); }
  }, [sku.id]);

  useEffect(() => { refresh(); }, [refresh]);

  const save = async () => {
    setBusy(true);
    try {
      await v2Api.catalog.createSkuRate(sku.id, {
        sku_id: sku.id,
        channel: form.channel || "default",
        valid_from: form.valid_from,
        valid_to: form.valid_to || null,
        list_price_inr: form.list_price_inr === "" ? null : Number(form.list_price_inr),
        base_price_inr: form.base_price_inr === "" ? null : Number(form.base_price_inr),
        currency: "INR",
        notes: form.notes || null,
      });
      setAdding(false);
      setForm({ valid_from: todayISO(), valid_to: "", list_price_inr: "", base_price_inr: "", channel: "default", notes: "" });
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-2 p-3 bg-slate-50 border rounded space-y-2 text-sm">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">
          Rate history · {sku.part_number}
        </p>
        {!adding && (
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus className="w-3.5 h-3.5 mr-1" /> New rate
          </Button>
        )}
      </div>

      {adding && (
        <div className="grid grid-cols-12 gap-2 items-end p-2 bg-white border rounded">
          <div className="col-span-2">
            <label className="text-[10px] text-slate-500">Valid from</label>
            <Input type="date" value={form.valid_from}
              onChange={(e) => setForm({ ...form, valid_from: e.target.value })} />
          </div>
          <div className="col-span-2">
            <label className="text-[10px] text-slate-500">Valid to</label>
            <Input type="date" value={form.valid_to}
              onChange={(e) => setForm({ ...form, valid_to: e.target.value })} />
          </div>
          <div className="col-span-2">
            <label className="text-[10px] text-slate-500">List price ₹</label>
            <Input type="number" value={form.list_price_inr}
              onChange={(e) => setForm({ ...form, list_price_inr: e.target.value })} />
          </div>
          <div className="col-span-2">
            <label className="text-[10px] text-slate-500">Base price ₹</label>
            <Input type="number" value={form.base_price_inr}
              onChange={(e) => setForm({ ...form, base_price_inr: e.target.value })} />
          </div>
          <div className="col-span-2">
            <label className="text-[10px] text-slate-500">Channel</label>
            <Input value={form.channel}
              onChange={(e) => setForm({ ...form, channel: e.target.value })} />
          </div>
          <div className="col-span-2 flex gap-1">
            <Button size="sm" onClick={save} disabled={busy}>Save</Button>
            <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {rates && rates.length === 0 && (
        <p className="text-xs italic text-slate-400">No rate history. Current denorm: list ₹{fmtINR(sku.list_price_inr)} · base ₹{fmtINR(sku.base_price_inr)}.</p>
      )}
      {rates && rates.length > 0 && (
        <table className="w-full text-xs">
          <thead className="text-slate-500">
            <tr>
              <th className="text-left py-1">Channel</th>
              <th className="text-left">Valid from</th>
              <th className="text-left">Valid to</th>
              <th className="text-right">List ₹</th>
              <th className="text-right">Base ₹</th>
              <th className="text-left">Source</th>
            </tr>
          </thead>
          <tbody>
            {rates.map((r) => (
              <tr key={r.id} className="border-t">
                <td className="py-1">{r.channel}</td>
                <td>{r.valid_from}</td>
                <td>{r.valid_to || "—"}</td>
                <td className="text-right">{fmtINR(r.list_price_inr)}</td>
                <td className="text-right">{fmtINR(r.base_price_inr)}</td>
                <td>{r.source}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function _daysSince(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

function _staleBadge(days) {
  if (days == null) return null;
  if (days > 180) return <Badge className="bg-rose-100 text-rose-700 border-rose-200">Stale · {days}d</Badge>;
  if (days > 90)  return <Badge className="bg-amber-100 text-amber-700 border-amber-200">Aging · {days}d</Badge>;
  return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Fresh · {days}d</Badge>;
}

function RatesTab({ skus }) {
  // view: "table" (compact, default) | "cards" (legacy verbose w/ history panel)
  const [view, setView] = useState("table");
  const [openSku, setOpenSku] = useState(null);

  if (skus.length === 0) {
    return <p className="text-sm italic text-slate-400">This product has no SKUs yet.</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">
          {skus.length} SKU{skus.length === 1 ? "" : "s"} · click any row to see / set rate history
        </p>
        <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-xs">
          <button
            type="button"
            onClick={() => setView("table")}
            className={`px-2 py-1 ${view === "table" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
          >Table</button>
          <button
            type="button"
            onClick={() => setView("cards")}
            className={`px-2 py-1 ${view === "cards" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
          >Cards</button>
        </div>
      </div>

      {view === "table" ? (
        <Card><CardContent className="py-0 px-0 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="px-3 py-2 text-left">Part #</th>
                <th className="px-3 py-2 text-left">SKU name</th>
                <th className="px-3 py-2 text-left">UOM</th>
                <th className="px-3 py-2 text-right">Base ₹</th>
                <th className="px-3 py-2 text-right">List ₹</th>
                <th className="px-3 py-2 text-right">GST %</th>
                <th className="px-3 py-2 text-right">Lead</th>
                <th className="px-3 py-2 text-left">Updated</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {skus.map((s) => {
                const days = _daysSince(s.updated_at);
                const isOpen = openSku === s.id;
                return (
                  <React.Fragment key={s.id}>
                    <tr
                      className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${isOpen ? "bg-blue-50/40" : ""}`}
                      onClick={() => setOpenSku(isOpen ? null : s.id)}
                    >
                      <td className="px-3 py-2 font-mono text-blue-700">{s.part_number}</td>
                      <td className="px-3 py-2 text-slate-900 max-w-xs truncate" title={s.name}>{s.name}</td>
                      <td className="px-3 py-2 text-slate-600">{s.uom}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtINR(s.base_price_inr)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtINR(s.list_price_inr)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{s.gst_rate_pct}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{s.lead_time_days != null ? `${s.lead_time_days}d` : "—"}</td>
                      <td className="px-3 py-2">{_staleBadge(days)}</td>
                      <td className="px-3 py-2 text-right">
                        <span className="text-blue-600 text-[10px]">{isOpen ? "Hide" : "Edit"}</span>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={9} className="px-3 py-3 bg-slate-50 border-t border-slate-100">
                          <SkuRatesPanel sku={s} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {skus.map((s) => (
            <Card key={s.id}><CardContent className="py-3">
              <button
                onClick={() => setOpenSku(openSku === s.id ? null : s.id)}
                className="w-full text-left flex items-center justify-between gap-2"
              >
                <div className="min-w-0">
                  <p className="text-sm text-slate-900">
                    <span className="font-mono text-xs text-blue-700">{s.part_number}</span>
                    {" · "}{s.name}
                  </p>
                  <p className="text-[10px] text-slate-500">
                    {s.uom} · list ₹{fmtINR(s.list_price_inr)} · base ₹{fmtINR(s.base_price_inr)} · GST {s.gst_rate_pct}%
                    {s.lead_time_days != null && ` · lead ${s.lead_time_days}d`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {_staleBadge(_daysSince(s.updated_at))}
                  <Badge variant="outline">{openSku === s.id ? "Hide" : "Rate history"}</Badge>
                </div>
              </button>
              {openSku === s.id && <SkuRatesPanel sku={s} />}
            </CardContent></Card>
          ))}
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// Where used
// --------------------------------------------------------------------------

function WhereUsedTab({ productId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    v2Api.catalog.whereUsed(productId).then(setData).catch(() => setData({ rfp_ids: [], historical_bid_count: 0, boq_line_count: 0 }));
  }, [productId]);
  if (!data) return <p className="text-sm text-slate-400">Loading…</p>;
  return (
    <div className="space-y-2">
      <Card><CardContent className="py-3 flex items-center justify-between">
        <span className="text-sm text-slate-700">Historical bids tagged to this product</span>
        <Badge>{data.historical_bid_count}</Badge>
      </CardContent></Card>
      <Card><CardContent className="py-3 flex items-center justify-between">
        <span className="text-sm text-slate-700">BoQ lines matched to a SKU of this product</span>
        <Badge>{data.boq_line_count}</Badge>
      </CardContent></Card>
      <Card><CardContent className="py-3">
        <p className="text-sm text-slate-700 mb-1">RFPs referencing this product</p>
        {data.rfp_ids.length === 0
          ? <p className="text-xs italic text-slate-400">None.</p>
          : <ul className="text-xs font-mono text-slate-600 space-y-0.5">
              {data.rfp_ids.map((id) => <li key={id}>{id}</li>)}
            </ul>}
      </CardContent></Card>
    </div>
  );
}

// --------------------------------------------------------------------------
// Page
// --------------------------------------------------------------------------

export default function ProductDetailPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try { setDetail(await v2Api.catalog.productDetail(productId)); }
    catch (err) { toast.error(err?.response?.data?.detail ?? "Could not load product"); }
    finally { setLoading(false); }
  }, [productId]);

  useEffect(() => { refresh(); }, [refresh]);

  if (loading && !detail) {
    return <div className="p-8 text-sm text-slate-500">Loading product…</div>;
  }
  if (!detail) {
    return <div className="p-8 text-sm text-slate-500">Not found.</div>;
  }

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-4">
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <button
            onClick={() => navigate("/v2/catalog")}
            className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-1"
          >
            <ArrowLeft className="w-3 h-3" /> Back to catalog
          </button>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Package className="w-5 h-5 text-blue-600" />
            {detail.name}
            <span className="text-sm text-slate-400 font-mono">({detail.code})</span>
          </h1>
          <p className="text-sm text-slate-500">
            {detail.category ? `${detail.category} · ` : ""}
            {detail.skus.length} SKU{detail.skus.length === 1 ? "" : "s"}
          </p>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview"><Package className="w-3.5 h-3.5 mr-1" />Overview</TabsTrigger>
          <TabsTrigger value="rates"><IndianRupee className="w-3.5 h-3.5 mr-1" />Rates</TabsTrigger>
          <TabsTrigger value="where-used"><Activity className="w-3.5 h-3.5 mr-1" />Where used</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-3">
          {detail.description && (
            <Card><CardContent className="py-3">
              <p className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-1">Description</p>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{detail.description}</p>
            </CardContent></Card>
          )}
          <Card><CardContent className="py-3">
            <p className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-2">SKUs</p>
            {detail.skus.length === 0
              ? <p className="text-sm italic text-slate-400">No SKUs.</p>
              : <div className="space-y-1">
                  {detail.skus.map((s) => (
                    <div key={s.id} className="flex items-center justify-between text-sm py-1 border-b last:border-b-0">
                      <div>
                        <span className="font-mono text-xs text-blue-700">{s.part_number}</span>{" · "}{s.name}
                      </div>
                      <div className="text-xs text-slate-500">
                        ₹{fmtINR(s.list_price_inr)} · GST {s.gst_rate_pct}%
                      </div>
                    </div>
                  ))}
                </div>}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="rates">
          <RatesTab skus={detail.skus} />
        </TabsContent>

        <TabsContent value="where-used">
          <WhereUsedTab productId={productId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
